import { TenantLanguage, TenantSecondaryLanguagesInner } from '@askporter/client-grieg-lyric';

type LanguageLike = TenantLanguage | TenantSecondaryLanguagesInner;

/**
 * Check if string has only upper case letters
 *
 * @param str - string to check
 * @returns boolean
 */
export const isUpper = (str: string): boolean => {
  return !/[a-z]/.test(str) && /[A-Z]/.test(str);
};

/**
 * Find the best matching language code from a list of tenant languages given a browser language string.
 * Tries exact match first, then falls back to a prefix (language-only) match.
 * e.g. browser 'en-US' will match tenant 'en-GB' via prefix.
 *
 * @param tenantLanguages - list of languages to match against
 * @param browserLang - a single BCP-47 tag from navigator.languages
 * @returns the matching tenant languageCode, or undefined if no match
 */
export const matchBrowserLanguage = (tenantLanguages: Array<LanguageLike>, browserLang: string): string | undefined => {
  const exact = tenantLanguages?.find((l) => l.languageCode === browserLang);
  if (exact?.languageCode) return exact.languageCode;
  const prefix = tenantLanguages?.find((l) => l.languageCode?.slice(0, 2) === browserLang?.slice(0, 2));
  return prefix?.languageCode;
};

/**
 * Initialise the user language. Priority order:
 * 1. localStorage (if present and in modern format e.g. en-GB)
 * 2. Browser language preference (navigator.languages ordered list matched against tenant languages)
 * 3. Tenant default language
 * 4. Fallback language (en-GB)
 *
 * @param tenantLanguages - primary tenant languages, used for default language fallback
 * @param fallbackLanguage - language code to use if no match found
 * @param allLanguages - combined primary and secondary languages, used for browser language matching
 * @returns a string containing the language code
 */
const initUserLanguage = (
  tenantLanguages: Array<TenantLanguage>,
  fallbackLanguage: string,
  allLanguages: Array<LanguageLike> = tenantLanguages,
): string => {
  // If the user already has a language stored in local storage then use that
  // also check that they don't have the legacy format e.g. en-gb
  // if so then we should re-initialise
  try {
    const selectedLang = localStorage.getItem('selectedLanguage');

    if (selectedLang) {
      const country = selectedLang.split('-');
      if (country[1] && isUpper(country[1])) {
        return selectedLang;
      }
    }
  } catch (err) {
    // if localStorage not available then carry on
  }

  // Check browser language preferences (ordered list) against tenant languages
  const browserLanguages = (
    window.navigator?.languages?.length ? [...window.navigator.languages] : [window.navigator?.language]
  ).filter(Boolean) as string[];

  let browserMatch: string | undefined;
  for (const browserLang of browserLanguages) {
    browserMatch = matchBrowserLanguage(allLanguages, browserLang);
    if (browserMatch) break;
  }

  // Check if tenantLanguages exist, if so filter the array to only contain the default language else assign an empty
  // array
  const tenantDefaultLang = tenantLanguages ? tenantLanguages.filter((lang) => lang.defaultLanguage) : [];

  // To handle an instance where there is more than one language marked as default, we only select the first item. If
  // no items exist then return the fall back language
  const language =
    browserMatch ?? (tenantDefaultLang.length > 0 ? tenantDefaultLang[0].languageCode : fallbackLanguage);

  // As the user didn't have the language stored in local storage, set it
  localStorage.setItem('selectedLanguage', language);

  return language;
};

export default initUserLanguage;
